/**
 * Shared date-display helper for UAT-standard MM-DD-YYYY output.
 *
 * Rules (per UAT feedback, September 2026):
 *   - Any user-facing date must render as MM-DD-YYYY (zero-padded).
 *   - Unknown / missing dates need not be shown → return `""` for null/empty
 *     input so callers can fall back to their own placeholder ("--", etc.).
 *   - If the input is a non-empty but unparseable string, return the trimmed
 *     original so downstream displays never silently lose data.
 *
 * Accepts ISO date strings ("2026-05-31"), ISO datetimes
 * ("2026-05-31T14:30:00Z"), and common alternate formats parseable by
 * `new Date(...)`. For date-only inputs the value is treated as UTC to avoid
 * timezone drift moving the displayed day.
 */
export function formatMDY(value: string | null | undefined): string {
    if (value === null || value === undefined) {
        return '';
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
        return '';
    }

    // Date-only ISO ("YYYY-MM-DD") — parse as UTC so the day never shifts
    // depending on the browser's local timezone.
    const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (isoDateOnly) {
        const [, year, month, day] = isoDateOnly;
        return `${month}-${day}-${year}`;
    }

    // Try `Date` for anything with a time component or a slash/dash date.
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return trimmed;
    }

    // For values that carried a time component we assume the caller wants the
    // UTC calendar day (matches how the backend stamps timestamps).
    const hasTimeComponent = /[T\s]\d{1,2}:\d{2}/.test(trimmed);
    const year = hasTimeComponent ? parsed.getUTCFullYear() : parsed.getFullYear();
    const monthIdx = hasTimeComponent ? parsed.getUTCMonth() : parsed.getMonth();
    const day = hasTimeComponent ? parsed.getUTCDate() : parsed.getDate();

    const mm = String(monthIdx + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${mm}-${dd}-${year}`;
}
